import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import houseHoldDatabaseKeys from "HouseholdDatabase/API/keys";
import { useHouseholdProfileContext } from "HouseholdProfile/HouseholdProfile";
import { useFetchProfileData } from "UserProfile/API/queries";

import { useUserID } from "Utilities/urlParamHooks";

import { getHouseholdDataByUserID, getHouseholdDetail, setHouseholdName, setPrimaryNetwork } from "./fetch";
import householdProfileKeys from "./keys";

const useGetHouseholdDetail = () => {
  const userID = useUserID();

  // watches isLoading because a user w/o a household gets one on the fly
  // but they need one for getHouseholdDetail to return anything (that's the theory). 
  // So we need to wait for them to have a household
  const { isLoading } = useFetchProfileData({ userID });

  return useQuery({
    queryKey: householdProfileKeys.householdDetail(userID),
    queryFn: () => getHouseholdDetail({ userID }),
    retry: false,
    enabled: Boolean(userID) && !isLoading,
  });
};

const useGetHouseholdDataByUserID = (userID, onSuccess) => {
  return useQuery({
    queryKey: householdProfileKeys.getHouseholdDataByUserID(userID),
    queryFn: () => (userID ? getHouseholdDataByUserID(userID) : () => {}),
    onSuccess: onSuccess && onSuccess,
    retry: false,
  });
};

const useSetPrimaryNetwork = () => {
  const queryClient = useQueryClient();
  const userID = useUserID();
  const { setPrimaryNetworkSetError, householdID } =
    useHouseholdProfileContext();

  return useMutation({
    mutationKey: householdProfileKeys.setPrimaryNetwork(householdID),
    mutationFn: async (networkID) => {
      setPrimaryNetworkSetError("");
      const { householdNetworks } = await setPrimaryNetwork({
        householdID,
        networkID,
      });
      const queryKey = householdProfileKeys.householdDetail(userID);
      const databaseQueryKey = houseHoldDatabaseKeys.households();
      const householdData = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, {
        ...householdData,
        householdProfile: {
          ...householdData.householdProfile,
          networks: householdNetworks,
        },
      });
      queryClient.invalidateQueries(databaseQueryKey);
    },
    onError: (err) => {
      setPrimaryNetworkSetError(
        "Something went wrong with setting that primary network."
      );
    },
  });
};

const useSetHousholdName = () => {
  const queryClient = useQueryClient();
  const { setErrorMessage, householdID } = useHouseholdProfileContext();
  const userID = useUserID();

  return useMutation({
    mutationKey: householdProfileKeys.setHouseholdName(householdID),
    mutationFn: async (name) => {
      setErrorMessage("");
      await setHouseholdName({ userID, name });
      const queryKey = householdProfileKeys.householdDetail(userID);
      const householdData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, {
        ...householdData,
        householdProfile: {
          ...householdData.householdProfile,
          name,
          derivedData: true,
        },
      });
      queryClient.invalidateQueries(queryKey);
    },
    onError: (err) => {
      setErrorMessage("Something went wrong with setting that household name.");
    },
  });
};

export {
  useGetHouseholdDetail,
  useSetPrimaryNetwork,
  useGetHouseholdDataByUserID,
  useSetHousholdName,
};